'use client';
import { useAppContext } from '@/context/app';
import useIsMobile from '@/hooks/use-is-mobile';
import { useIsMobileSsr } from '@/hooks/use-is-mobile-ssr';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import MiniCartLazyLoad from '../MiniCart/mini-cart-lazy-load';
import NavigationLazyLoad from '../Navigation/navigation-lazy-load';
import HeaderToolbar from './headerToolbar';
import NavBar from './nav-bar';
import NavMenu from './nav-menu';
import NavTrigger from './navTrigger';

const HEADER_ID = 'aw-app-header';

const HeaderClient = props => {
  const { announcements, colorMenus } = props;
  const pathname = usePathname();
  const [miniCartOpen, setMiniCartOpen] = useState(false);
  const [showNoItemDrawer, setShowNoItemDrawer] = useState(false);
  const [lastAddToCartTimestamp, setLastAddToCartTimestamp] = useState(() => new Date().toString());
  const [isForAddToCartOk, setIsForAddToCartOk] = useState(false);
  const [
    { headStickedOnTop, headerToolbarHeight },
    {
      actions: { toggleHeadStickedOnTop },
    },
  ] = useAppContext();
  const { isDesktop } = useIsMobileSsr();
  const isMobile = useIsMobile();
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const navTriggerRef = useRef(null);
  const shouldResetHeaderOnTopRef = useRef(false);

  const prevPropsRef = useRef({
    miniCartOpen,
  });

  if (prevPropsRef.current.miniCartOpen !== miniCartOpen) {
    prevPropsRef.current.miniCartOpen = miniCartOpen;

    if (!miniCartOpen) {
      setIsForAddToCartOk(false);
    }
  }

  const getShouldResetHeaderOnTop = useCallback(() => shouldResetHeaderOnTopRef.current, []);
  const setShouldResetHeaderOnTop = useCallback(nextState => (shouldResetHeaderOnTopRef.current = nextState), []);

  useEffect(() => {
    const scrollAction = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      setMiniCartOpen(true);
      setLastAddToCartTimestamp(Date.now().toString());
      setIsForAddToCartOk(true);
    };

    document.addEventListener('CART_ADD_ITEM', scrollAction);

    return () => {
      document.removeEventListener('CART_ADD_ITEM', scrollAction);
    };
  }, []);

  const getNavTriggerElement = useCallback(() => navTriggerRef.current, []);

  const handleTriggerSetMobileMenu = nextState => {
    let nextOpen = nextState;

    if (typeof stateAction === 'function') {
      nextOpen = nextState(mobileMenuVisible);
    }

    if (nextOpen) {
      toggleHeadStickedOnTop(true);
      setShouldResetHeaderOnTop(true);
    }

    setMobileMenuVisible(nextOpen);
  };

  if (pathname === '/checkout') {
    return null;
  }

  return (
    <>
      <header
        className={clsx(' duration-[400ms] sticky top-0 z-40 h-auto  w-full bg-white transition-all')}
        style={{
          transform: headStickedOnTop ? undefined : `translateY(-${headerToolbarHeight}px)`,
        }}
        id={HEADER_ID}
      >
        <HeaderToolbar
          announcements={announcements}
          miniCartOpen={miniCartOpen}
          setMiniCartOpen={setMiniCartOpen}
          setShowNoItemDrawer={setShowNoItemDrawer}
          headerId={HEADER_ID}
          getShouldResetHeaderOnTop={getShouldResetHeaderOnTop}
          setShouldResetHeaderOnTop={setShouldResetHeaderOnTop}
          navTrigger={
            <NavTrigger
              setMobileMenuVisible={handleTriggerSetMobileMenu}
              mobileMenuVisible={mobileMenuVisible}
              ref={navTriggerRef}
            />
          }
        />
        {isDesktop && (
          <NavBar
            navMenu={<NavMenu colorMenus={colorMenus} />}
            miniCartOpen={miniCartOpen}
            setMiniCartOpen={setMiniCartOpen}
            setShowNoItemDrawer={setShowNoItemDrawer}
          />
        )}
      </header>
      {isMobile && (
        <NavigationLazyLoad
          open={mobileMenuVisible}
          onOpenChange={setMobileMenuVisible}
          getNavTriggerElement={getNavTriggerElement}
        />
      )}
      <MiniCartLazyLoad
        isOpen={miniCartOpen}
        setIsOpen={setMiniCartOpen}
        showNoItemDrawer={showNoItemDrawer}
        setShowNoItemDrawer={setShowNoItemDrawer}
        lastAddToCartTimestamp={lastAddToCartTimestamp}
        isForAddToCartOk={isForAddToCartOk}
      />
    </>
  );
};

export default HeaderClient;
